/**
 * Does an ops Command-Center session hold WRITE, or only READ_ONLY?
 *
 * Extracted from `AuthContext.canWrite` so the one comparison this whole gate
 * rests on can be pinned by tests — the context itself is a React provider and
 * the frontend test runner is node-environment, so nothing inside it is
 * reachable from a test.
 *
 * The failure modes are asymmetric, which is why it is worth a test at all:
 * writing `level === 'READ_ONLY'` instead of `level !== 'WRITE'` looks
 * equivalent and behaves identically today, but any unknown or missing level
 * would then read as WRITABLE — the UI offering mutations the server refuses.
 * Anchoring on WRITE means anything unrecognised fails closed.
 */

/** The ops access levels the backend mints (`AccessTokenLevel`). */
export type OpsAccessLevel = 'READ_ONLY' | 'WRITE';

export interface OpsWriteAccessInput {
  /** `user.type` from the auth context — only 'ops' sessions carry a level. */
  userType?: 'org' | 'ops' | null;
  /** `opsOrgAccess?.level`; absent until the sessionStorage restore lands. */
  level?: OpsAccessLevel | string | null;
}

/**
 * True when this session is an ops one that may NOT mutate.
 *
 * Org users are never read-only by this test — their limits are capabilities,
 * which `can()` already answers. An ops session with no org access yet reads as
 * read-only for the render before the restore effect runs: closed, then open.
 */
export function isReadOnlyOpsSession({ userType, level }: OpsWriteAccessInput): boolean {
  if (userType !== 'ops') return false;
  return level !== 'WRITE';
}
